import React from 'react';
import { 
  ServerIcon, 
  CircleStackIcon, 
  GlobeAltIcon, 
  CloudIcon,
  CodeBracketIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Skills: React.FC = () => {
  const skillCategories = [
    {
      title: "Backend Development",
      icon: ServerIcon,
      color: "bg-red-500",
      skills: ["Laravel", "PHP", "Node.js", "Octane/Swoole"]
    },
    {
      title: "Frontend Development", 
      icon: GlobeAltIcon,
      color: "bg-blue-500",
      skills: ["Vue", "React", "TypeScript", "Tailwind", "Alpine", "Bootstrap", "Vuetifyjs"]
    },
    {
      title: "Database & Storage",
      icon: CircleStackIcon,
      color: "bg-green-500", 
      skills: ["MySQL", "PostgreSQL", "MongoDB", "Redis", "ElasticSearch", "Algolia"]
    },
    {
      title: "Cloud & DevOps",
      icon: CloudIcon,
      color: "bg-purple-500",
      skills: ["AWS", "Docker", "CI/CD", "Laravel Forge", "Laravel Vapor"]
    },
    {
      title: "APIs & Integration",
      icon: CodeBracketIcon,
      color: "bg-yellow-500",
      skills: ["RESTful APIs", "Stripe", "Twilio", "OpenAI", "DeepSeek","Cashier", "Scout", "Sail", "Nova", "Face++"]
    },
    {
      title: "Testing & Quality",
      icon: ShieldCheckIcon,
      color: "bg-indigo-500",
      skills: ["Pest", "PHPUnit", "TDD", "Laravel Dusk", "Jest"]
    }
  ];

  const technologies = [
    "Laravel", "Vue", "React", "TypeScript", "Tailwind CSS", "PHP", 
    "MySQL", "PostgreSQL", "Redis", "AWS", "Docker", "Node.js",
    "MongoDB", "ElasticSearch", "Stripe", "OpenAI", "Algolia", "Git"
  ];

  return (
    <section id="skills" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Technical <span className="gradient-text">Expertise</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive skill set spanning frontend, backend, cloud architecture, and modern development practices
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {skillCategories.map((category, index) => (
            <div 
              key={category.title}
              className="bg-gray-50 rounded-xl p-6 card-shadow hover:scale-105 transition-transform duration-300"
            >
              <div className="flex items-center mb-4">
                <div className={`${category.color} p-3 rounded-lg mr-4`}>
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{category.title}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill) => (
                  <span 
                    key={skill}
                    className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700 border border-gray-200"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Core Technologies</h3>
            <p className="text-gray-600">Technologies I work with daily</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {technologies.map((tech) => (
              <div 
                key={tech}
                className="bg-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <span className="text-sm font-semibold text-gray-800">{tech}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
          <div className="bg-primary-50 rounded-xl p-6">
            <div className="text-3xl font-bold text-primary-600 mb-2">10+</div>
            <div className="text-gray-700 font-medium">Years Experience</div>
          </div>
          <div className="bg-green-50 rounded-xl p-6">
            <div className="text-3xl font-bold text-green-600 mb-2">30+</div>
            <div className="text-gray-700 font-medium">Projects Delivered</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-6">
            <div className="text-3xl font-bold text-yellow-600 mb-2">250%</div>
            <div className="text-gray-700 font-medium">Performance Increase</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-6">
            <div className="text-3xl font-bold text-purple-600 mb-2">10x</div>
            <div className="text-gray-700 font-medium">Cost Reduction</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;